import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Briefcase, Building2, HeartHandshake, Scale, ScrollText, ShieldCheck } from "lucide-react";
import { Loading } from "@/components/common";
import { useTranslation } from "react-i18next";

const ICONS = { Briefcase, Scale, HeartHandshake, Building2, ShieldCheck, ScrollText };

export default function PracticeAreas() {
  const { t } = useTranslation();
  const { profile } = useOutletContext() || {};
  if (!profile) return <Loading label={t("practice.loading")} />;

  return (
    <div data-testid="practice-areas-page">
      <section className="bg-navy text-ivory hero-grain relative py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-4">{profile.firm_name}</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-semibold tracking-tight max-w-3xl">{t("practice.title")}</h1>
          <p className="mt-6 text-ivory/70 max-w-2xl leading-relaxed">
            {t("practice.intro")}
          </p>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(profile.practice_areas || []).map((area, i) => {
            const Icon = ICONS[area.icon] || Scale;
            return (
              <motion.div key={area.id}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                data-testid={`practice-area-detail-${area.id}`}
                className="group bg-white border border-border p-8 sm:p-10 transition-all duration-300 hover:shadow-hovercard hover:border-gold hover:-translate-y-1">
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">0{i + 1}</span>
                <Icon className="h-9 w-9 text-gold-700 mt-4 mb-6" />
                <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-navy mb-4">{area.title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{area.description}</p>
                <Link to={`/book-consultation?area=${encodeURIComponent(area.title_en || area.title)}`}
                  data-testid={`practice-area-book-${area.id}`}
                  className="inline-flex items-center gap-2 mt-8 text-xs font-semibold uppercase tracking-wider text-navy group-hover:text-gold-700 transition-colors">
                  {t("practice.discuss")} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-20 bg-navy text-ivory p-10 sm:p-14 hero-grain relative">
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold">{t("practice.notSure")}</h2>
              <p className="mt-3 text-ivory/70 max-w-xl text-sm leading-relaxed">
                {t("practice.notSureBody")}
              </p>
            </div>
            <Link to="/book-consultation" data-testid="practice-areas-cta-btn"
              className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-gold-600 transition-colors duration-300 font-semibold uppercase tracking-wider text-sm px-8 py-4 shrink-0">
              {t("practice.book")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
